import React, { useState, useEffect } from "react";
import { NotificationManager as nm } from "react-notifications";
import Popup from "reactjs-popup";
import { deleteMatchDossier, updateMatchDossier } from "shared/services/api-assistant.js";
import BoxCard from "shared/components/box/BoxCard.js";
import ControllerClubs2 from "shared/components/controllers/ControllerClubs2";
import BoxMessage from "shared/components/box/BoxMessage.js";
import { getMatches } from "shared/services/api-mfl.js";
import ControllerMflMatchType from "shared/components/controllers/ControllerMflMatchType.js";
import ItemCardMatch from "shared/components/items/ItemCardMatch.js";

const PopupEditMatchDossier = ({
  trigger,
  dossier,
  onClose,
  onUpdate,
  userId,
  loadedMatches,
  setMatchesToLoad,
  onDelete,
}) => {
  const [name, setName] = useState(dossier?.name || "");
  const [matchIds, setMatchIds] = useState(dossier?.matches || []);
  const [availableMatches, setAvailableMatches] = useState(null);

  const [clubs, setClubs] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);

  const [selectedCriteria, setSelectedCriteria] = useState("All");
  const [loadingMatches, setLoadingMatches] = useState(false);

  const update = (close, params) => {
    updateMatchDossier({
      handleSuccess: (v) => {
        nm.info("The dossier has been updated");
        onUpdate?.();
        close();
      },
      params: {
        id: dossier.id,
        ...params,
      },
    });
  };

  const remove = (close, id) => {
    deleteMatchDossier({
      handleSuccess: (v) => {
        nm.info("The dossier has been deleted");
        if (onClose) onClose();
        if (onDelete) onDelete();
        if (close) close();
      },
      params: {
        id: id,
      },
    });
  };

  const fetchMatches = () => {
    if (selectedClub) {
      setLoadingMatches(true);

      getMatches({
        handleSuccess: (m) => {
          setAvailableMatches(m);
          setLoadingMatches(false);
        },
        handleError: (e) => console.log(e),
        params: {
          clubId: selectedClub.id,
          limit: 15,
          past: true,
          onlyCompetitions: selectedCriteria === "Competitions",
          onlyFriendliesHome: selectedCriteria === "Home friendlies",
        },
      });
    } else {
      setAvailableMatches(null);
    }
  };

  const closePopup = (close) => {
    if (close) close();
  };

  useEffect(() => {
    fetchMatches();
  }, [selectedClub, selectedCriteria]);

  return (
    <div className="PopupEditMatchDossier">
      <Popup
        trigger={trigger}
        modal
        closeOnDocumentClick={false}
        onClose={() => {
          closePopup();
          onClose?.();
        }}
        onOpen={() => {
          setName(dossier.name);
          setMatchesToLoad(dossier.matches ?? []);
        }}
        className={"fade-in popup-xl"}
      >
        {(close) => (
          <div>
            <div className="d-flex flex-row mb-3">
              <div className="flex-grow-1">
                <h2 className="text-white">Edit the dossier</h2>
              </div>
              <div className="flex-grow-0">
                <button className={"btn"} onClick={() => closePopup(close)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div className="d-flex flex-row mt-3">
              <BoxCard
                className="w-100"
                title="Name"
                content={
                  <input
                    type="text"
                    className="form-control w-100 me-1"
                    value={name}
                    onChange={(v) => setName(v.target.value)}
                    placeholder={"Dossier name"}
                    autoFocus
                  />
                }
              />
            </div>

            <div className="d-flex flex-fill flex-column flex-md-row" style={{ maxHeight: "500px" }}>
              <BoxCard
                className="flex-fill d-flex flex-column"
                title="Add matches"
                contentClassName="d-flex flex-fill flex-grow-1 overflow-hidden"
                content={
                  <div
                    className="d-flex flex-column flex-md-row flex-fill w-100 overflow-hidden"
                    style={{ minHeight: 0 }}
                  >
                    <div className="d-flex flex-grow-0 mb-2 mb-md-0 overflow-auto">
                      <ControllerClubs2
                        onSelect={(c) => setSelectedClub(c)}
                        userId={userId}
                        selectedClubId={selectedClub?.id}
                      />
                    </div>

                    <div className="d-flex flex-column flex-fill ps-md-3 overflow-hidden" style={{ minHeight: 0 }}>
                      {availableMatches ? (
                        <div className="d-flex flex-column flex-fill overflow-hidden" style={{ minHeight: 0 }}>
                          <div className="d-flex flex-grow-0 justify-content-end pb-2">
                            <ControllerMflMatchType
                              selectedCriteria={selectedCriteria}
                              onChange={(c) => setSelectedCriteria(c)}
                            />
                          </div>

                          <div
                            className="d-flex flex-column flex-fill background-color overflow-hidden"
                            style={{ minHeight: 0 }}
                          >
                            <div className="w-100 h-100 overflow-auto">
                              {availableMatches.map((m) => (
                                <div key={m.id} className="Item d-flex flex-row">
                                  <div className="d-flex flex-fill flex-grow-1">
                                    <ItemCardMatch match={m} selected={matchIds?.indexOf(m.id) >= 0} />
                                  </div>

                                  <div className="d-flex flex-grow-0">
                                    <button
                                      className="btn btn-link btn-sm text-white"
                                      disabled={matchIds?.indexOf(m.id) >= 0}
                                      onClick={() => {
                                        setMatchesToLoad([m.id]);

                                        setMatchIds([...matchIds, m.id]);
                                      }}
                                    >
                                      <i className="bi bi-arrow-right"></i>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <BoxMessage content={"No club selected"} className="background-color flex-fill" />
                      )}
                    </div>
                  </div>
                }
              />
              <BoxCard
                className="flex-fill d-flex flex-column"
                title="Selected matches"
                contentClassName="d-flex flex-fill flex-grow-1 overflow-hidden"
                content={
                  <div
                    className="d-flex flex-column flex-fill background-color w-100 overflow-hidden"
                    style={{ minHeight: 0 }}
                  >
                    <div className="w-100 h-100 overflow-auto">
                      {Object.keys(loadedMatches).map((m) => {
                        if (matchIds.map((i) => i + "")?.indexOf(m) >= 0) {
                          return (
                            <div key={m} className="Item d-flex flex-row">
                              <div className="d-flex flex-fill flex-grow-1">
                                <ItemCardMatch match={loadedMatches[m]} />
                              </div>

                              <div className="d-flex flex-grow-0">
                                <button
                                  className="btn btn-link btn-sm text-white"
                                  onClick={() => setMatchIds(matchIds.filter((o) => o !== parseInt(m, 10)))}
                                >
                                  <i className="bi bi-trash3-fill text-danger"></i>
                                </button>
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                }
              />
            </div>

            <div className="d-flex flex-row justify-content-end mt-3">
              <button className="btn btn-danger text-white me-1" onClick={() => remove(close, dossier.id)}>
                <i className="bi bi-trash3" /> Delete
              </button>
              <button className="btn btn-info text-white" onClick={() => update(close, { name, matches: matchIds })}>
                <i className="bi bi-floppy2-fill"></i> Save
              </button>
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
};

export default PopupEditMatchDossier;
